import React, { useEffect, useState } from 'react';
import { Link, useParams, useLocation } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { MessageLocalExpertButton, clearProfileCache } from './ProfileLink';
import UserAvatar from './UserAvatar';
import { ReviewCard } from './ReviewComponents';
import { formatRentalDateRange } from './dateUtils';
import {
  canShowRentalPaymentLink,
  GEAR_IMAGE_PLACEHOLDER,
  getRentalPaymentAmount,
  OWNER_SHARE_PERCENT,
  PLATFORM_FEE_PERCENT,
} from './rentalPayments';
import { PayButton } from './PaymentButton';
import { useToast } from './Toast';
import { db, storage, functions } from './firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { compressImage } from './imageCompression';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  setDoc,
  where,
} from 'firebase/firestore';
import {
  loadPrivateProfile,
  migratePrivateFieldsFromPublic,
  savePrivateProfile,
  splitPublicAndPrivateProfile,
} from './userPrivateProfile';

const cardStyle = {
  padding: '24px',
  border: '1px solid #e5e7eb',
  borderRadius: '16px',
  backgroundColor: '#ffffff',
  boxShadow: '0 4px 20px rgba(0,0,0,0.07)',
};

const inputStyle = {
  width: '100%',
  padding: '14px',
  borderRadius: '10px',
  border: '1px solid #d1d5db',
  fontSize: '1rem',
  boxSizing: 'border-box',
};

function extractFirstName(name, fallback = 'Member') {
  const normalized = String(name || '').trim();
  if (!normalized) return fallback;
  return normalized.split(/\s+/)[0] || fallback;
}

function getPublicName(profile, fallback = 'Member') {
  if (!profile) return extractFirstName(fallback, fallback);
  const fullName = typeof profile === 'string'
    ? profile
    : (profile.publicName || profile.userName || profile.displayName || fallback);
  return extractFirstName(fullName, fallback);
}

function getFirstName(profile, fallback = 'Member') {
  return getPublicName(profile, fallback);
}

function UserProfile({ currentUser, onProfileUpdate }) {
  const { showToast } = useToast();
  const { userId: routeUserId } = useParams();
  const routerLocation = useLocation();
  const profileUserId = routeUserId || currentUser?.uid;
  const isOwnProfile = currentUser?.uid === profileUserId;

  const [profile, setProfile] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [gearList, setGearList] = useState([]);
  const [approvedUnpaidRentals, setApprovedUnpaidRentals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [stripeStatus, setStripeStatus] = useState(null);
  const [stripeLoading, setStripeLoading] = useState(false);
  const [stripeMessage, setStripeMessage] = useState('');

  const [publicName, setPublicName] = useState('');
  const [aboutMe, setAboutMe] = useState('');
  const [location, setLocation] = useState('');
  const [phone, setPhone] = useState('');
  const [localExpert, setLocalExpert] = useState(false);

  useEffect(() => {
    if (!profileUserId) {
      setLoading(false);
      return;
    }

    const loadProfile = async () => {
      setLoading(true);
      try {
        const userSnap = await getDoc(doc(db, 'users', profileUserId));
        let userData = userSnap.exists() ? userSnap.data() : {};
        let privateData = {};

        if (isOwnProfile) {
          privateData = await loadPrivateProfile(profileUserId);
          userData = await migratePrivateFieldsFromPublic(profileUserId, userData);
          if (!privateData.email && !privateData.phone) {
            privateData = await loadPrivateProfile(profileUserId);
          }

          setPublicName(
            userData.publicName ||
            userData.userName ||
            currentUser?.displayName ||
            ''
          );
          setAboutMe(userData.aboutMe || '');
          setLocation(userData.location || '');
          setPhone(privateData.phone || currentUser?.phoneNumber || '');
          setLocalExpert(Boolean(userData.localExpert));
          setProfile({
            id: profileUserId,
            ...userData,
            email: privateData.email || currentUser?.email || '',
            phone: privateData.phone || '',
          });
        } else {
          const { publicData } = splitPublicAndPrivateProfile(userData);
          setProfile({ id: profileUserId, ...publicData });
        }

        const reviewsQuery = query(
          collection(db, 'reviews'),
          where('reviewedId', '==', profileUserId)
        );
        const reviewsSnap = await getDocs(reviewsQuery);
        setReviews(reviewsSnap.docs.map((d) => ({ id: d.id, ...d.data() })));

        const gearQuery = query(
          collection(db, 'gear'),
          where('ownerId', '==', profileUserId)
        );
        const gearSnap = await getDocs(gearQuery);
        setGearList(gearSnap.docs.map((d) => ({ id: d.id, ...d.data() })));

        if (isOwnProfile) {
          const rentalQuery = query(
            collection(db, 'rentals'),
            where('renterId', '==', profileUserId),
            where('status', '==', 'approved')
          );
          const rentalSnap = await getDocs(rentalQuery);
          const unpaidRentals = [];

          for (const rentalDoc of rentalSnap.docs) {
            const rental = { id: rentalDoc.id, ...rentalDoc.data() };
            let gear = null;

            if (rental.gearId) {
              const gearSnap = await getDoc(doc(db, 'gear', rental.gearId));
              if (gearSnap.exists()) {
                gear = { id: gearSnap.id, ...gearSnap.data() };
              }
            }

            if (!canShowRentalPaymentLink(rental, gear)) continue;

            unpaidRentals.push({
              ...rental,
              paymentAmount: getRentalPaymentAmount(rental, gear),
            });
          }

          setApprovedUnpaidRentals(unpaidRentals);
        } else {
          setApprovedUnpaidRentals([]);
        }
      } catch (error) {
        console.error('Error loading profile:', error);
      } finally {
        setLoading(false);
      }
    };

    loadProfile();
  }, [profileUserId, isOwnProfile, currentUser]);

  useEffect(() => {
    if (!isOwnProfile || !currentUser) {
      setStripeStatus(null);
      return;
    }

    const loadStripeStatus = async () => {
      try {
        const getStatus = httpsCallable(functions, 'getStripeConnectStatus');
        const result = await getStatus();
        setStripeStatus(result.data || null);
      } catch (error) {
        console.error('Stripe status error:', error);
      }
    };

    loadStripeStatus();
  }, [isOwnProfile, currentUser, routerLocation.search]);

  useEffect(() => {
    const stripeParam = new URLSearchParams(routerLocation.search).get('stripe');
    if (stripeParam === 'complete') {
      setStripeMessage('Stripe setup saved. We will send your rental payouts once your account is fully verified.');
    } else if (stripeParam === 'refresh') {
      setStripeMessage('Please finish connecting Stripe to receive rental payouts.');
    }
  }, [routerLocation.search]);

  const handleConnectStripe = async () => {
    if (!isOwnProfile || !currentUser) return;

    setStripeLoading(true);
    setStripeMessage('');
    try {
      const createLink = httpsCallable(functions, 'createStripeConnectLink');
      const result = await createLink();
      if (result.data?.url) {
        window.location.assign(result.data.url);
        return;
      }
      throw new Error('Could not start Stripe onboarding.');
    } catch (error) {
      const friendlyMessage =
        error?.message && error.message !== 'INTERNAL'
          ? error.message
          : 'Could not connect Stripe. Please try again or contact support.';
      setStripeMessage(friendlyMessage);
      console.error('Stripe Connect error:', error);
    } finally {
      setStripeLoading(false);
    }
  };

  const renterReviews = reviews.filter((review) => review.type === 'owner-review');
  const averageRating = renterReviews.length
    ? (renterReviews.reduce((sum, r) => sum + (r.rating || 0), 0) / renterReviews.length).toFixed(1)
    : null;

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !isOwnProfile || !currentUser) return;

    setUploadingPhoto(true);
    try {
      // Converts HEIC from Apple devices and shrinks large photos; without this
      // an iPhone upload can be a profile picture nobody can see.
      const prepared = await compressImage(file);
      const path = `profiles/${currentUser.uid}/${Date.now()}_${prepared.name}`;
      const storageRef = ref(storage, path);
      await uploadBytes(storageRef, prepared);
      const profilePhotoUrl = await getDownloadURL(storageRef);

      await setDoc(
        doc(db, 'users', currentUser.uid),
        {
          userName: profile?.userName || currentUser.displayName || '',
          profilePhotoUrl,
          createdAt: profile?.createdAt || new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        },
        { merge: true }
      );
      clearProfileCache(currentUser.uid);
      setProfile((prev) => ({ ...prev, profilePhotoUrl }));
    } catch (error) {
      showToast('Could not upload photo: ' + error.message, 'error');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleSaveProfile = async () => {
    if (!isOwnProfile || !currentUser) return;

    const trimmedName = publicName.trim();
    if (!trimmedName) {
      showToast('Please enter the name you want others to see.', 'error');
      return;
    }

    setSaving(true);
    try {
      const { updateProfile } = await import('firebase/auth');
      const { auth } = await import('./firebase');

      const publicProfile = splitPublicAndPrivateProfile({
        userName: profile?.userName || trimmedName,
        publicName: trimmedName,
        aboutMe: aboutMe.trim(),
        location: location.trim(),
        localExpert,
        createdAt: profile?.createdAt || new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      }).publicData;

      await Promise.all([
        setDoc(doc(db, 'users', currentUser.uid), publicProfile, { merge: true }),
        savePrivateProfile(currentUser.uid, {
          email: currentUser.email || profile?.email || '',
          phone: phone.trim(),
        }),
      ]);

      if (auth.currentUser) {
        await updateProfile(auth.currentUser, { displayName: trimmedName });
      }

      clearProfileCache(currentUser.uid);

      const updatedProfile = {
        ...profile,
        publicName: trimmedName,
        aboutMe: aboutMe.trim(),
        location: location.trim(),
        phone: phone.trim(),
        localExpert,
      };
      setProfile(updatedProfile);
      onProfileUpdate?.(updatedProfile);
      showToast('Profile updated successfully!', 'success');
    } catch (error) {
      showToast('Could not save profile: ' + error.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  if (!currentUser && !routeUserId) {
    return (
      <p style={{ textAlign: 'center', padding: '80px', color: '#6b7280' }}>
        Please <Link to="/" style={{ color: '#3b82f6' }}>log in</Link> to view your profile.
      </p>
    );
  }

  if (loading) {
    return <p style={{ textAlign: 'center', padding: '80px' }}>Loading profile...</p>;
  }

  if (!profile) {
    return <p style={{ textAlign: 'center', padding: '80px' }}>Profile not found.</p>;
  }

  const displayName = getPublicName(profile, 'Member');
  const firstName = getFirstName(profile);
  const previewName = isOwnProfile && publicName.trim() ? publicName.trim() : displayName;

  return (
    <div style={{ maxWidth: '960px', margin: '40px auto', padding: '0 24px' }}>
      {isOwnProfile && approvedUnpaidRentals.length > 0 && (
        <div style={{ marginBottom: '24px', display: 'grid', gap: '16px' }}>
          {approvedUnpaidRentals.map((rental) => (
            <div
              key={rental.id}
              style={{
                padding: '20px 24px',
                borderRadius: '16px',
                backgroundColor: '#fffbeb',
                border: '2px solid #fbbf24',
                boxShadow: '0 8px 24px rgba(251, 191, 36, 0.18)',
              }}
            >
              <p style={{ margin: '0 0 8px 0', color: '#92400e', fontWeight: '800', fontSize: '1.05rem' }}>
                Rental Approved — Payment Needed
              </p>
              <p style={{ margin: '0 0 14px 0', color: '#78350f', lineHeight: '1.6' }}>
                Great news! Your rental for <strong>{rental.gearName || 'gear'}</strong> has been approved.
                Complete payment to lock in your dates
                {rental.rentalStartDate && rental.rentalEndDate
                  ? ` (${formatRentalDateRange(rental.rentalStartDate, rental.rentalEndDate)})`
                  : ''}.
              </p>
              <PayButton
                amount={rental.paymentAmount}
                gearName={rental.gearName || 'Gear Rental'}
                rentalData={{
                  gearId: rental.gearId,
                  rentalId: rental.id,
                  startDate: rental.rentalStartDate,
                  endDate: rental.rentalEndDate,
                }}
                fullWidth={false}
                style={{ marginTop: 0 }}
              />
            </div>
          ))}
        </div>
      )}

      <div style={{ ...cardStyle, display: 'flex', gap: '32px', flexWrap: 'wrap', alignItems: 'flex-start' }}>
        <div style={{ textAlign: 'center', flexShrink: 0 }}>
          <UserAvatar
            profile={profile}
            userId={profileUserId}
            name={displayName}
            size={160}
            borderWidth={4}
            alt={displayName}
            style={{ boxShadow: '0 4px 16px rgba(0,0,0,0.08)' }}
          />
          {isOwnProfile && (
            <label
              style={{
                display: 'inline-block',
                marginTop: '16px',
                padding: '10px 18px',
                backgroundColor: '#3b82f6',
                color: 'white',
                borderRadius: '8px',
                cursor: uploadingPhoto ? 'not-allowed' : 'pointer',
                fontSize: '0.9rem',
                fontWeight: '600',
              }}
            >
              {uploadingPhoto ? 'Uploading...' : 'Change Photo'}
              <input
                type="file"
                accept="image/*,.heic,.heif"
                onChange={handlePhotoUpload}
                style={{ display: 'none' }}
                disabled={uploadingPhoto}
              />
            </label>
          )}
        </div>

        <div style={{ flex: 1, minWidth: '260px' }}>
          <h1 style={{ margin: '0 0 8px 0', color: '#1f2937', fontSize: '2rem' }}>
            {isOwnProfile ? `Hi, ${getFirstName(previewName)}!` : displayName}
          </h1>
          {isOwnProfile && (
            <p style={{ margin: '0 0 12px 0', color: '#6b7280', fontSize: '0.95rem' }}>
              Others will see you as:{' '}
              <strong style={{ color: '#1f2937' }}>{getPublicName(previewName, previewName || '—')}</strong>
            </p>
          )}

          {(isOwnProfile ? localExpert : profile.localExpert) && (
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                marginBottom: '8px',
                padding: '6px 12px',
                borderRadius: '9999px',
                backgroundColor: '#ecfdf5',
                border: '1px solid #a7f3d0',
                color: '#047857',
                fontWeight: '700',
                fontSize: '0.85rem',
              }}
            >
              <span aria-hidden="true">🗺️</span>
              Local Expert
            </span>
          )}

          {!isOwnProfile && profile.localExpert && (
            <MessageLocalExpertButton
              user={currentUser}
              expertId={profileUserId}
              expertLocation={profile.location}
            />
          )}

          {isOwnProfile && localExpert && (
            <p style={{ margin: '0 0 12px 0', color: '#047857', fontSize: '0.9rem', lineHeight: '1.5' }}>
              Your profile shows a button so adventurers can ask you about local trails, activities, and hidden gems.
            </p>
          )}

          {averageRating ? (
            <p style={{ fontSize: '1.2rem', color: '#f59e0b', margin: '0 0 12px 0' }}>
              {'★'.repeat(Math.round(Number(averageRating)))}
              {'☆'.repeat(5 - Math.round(Number(averageRating)))}{' '}
              <span style={{ color: '#6b7280', fontSize: '1rem' }}>
                {averageRating} ({renterReviews.length} review{renterReviews.length !== 1 ? 's' : ''})
              </span>
            </p>
          ) : (
            <p style={{ color: '#9ca3af', margin: '0 0 12px 0' }}>No reviews yet</p>
          )}

          {profile.location && (
            <p style={{ margin: '6px 0', color: '#4b5563' }}>
              <strong>Location:</strong> {profile.location}
            </p>
          )}

          {profile.createdAt && (
            <p style={{ margin: '6px 0', color: '#6b7280', fontSize: '0.95rem' }}>
              Member since {new Date(profile.createdAt).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
            </p>
          )}

          <p style={{ margin: '6px 0', color: '#4b5563' }}>
            <strong>Gear listed:</strong> {gearList.length} item{gearList.length !== 1 ? 's' : ''}
          </p>

          {isOwnProfile && profile.email && (
            <p style={{ margin: '6px 0', color: '#4b5563' }}>
              <strong>Email:</strong> {profile.email}
            </p>
          )}
        </div>
      </div>

      {isOwnProfile && (gearList.length > 0 || stripeStatus?.connected) && (
        <div style={{ ...cardStyle, marginTop: '28px' }}>
          <h2 style={{ marginTop: 0, color: '#1f2937' }}>Rental Payouts (Stripe)</h2>
          {profile.platformFeeExempt ? (
            <p
              style={{
                margin: '0 0 16px 0',
                padding: '12px 14px',
                borderRadius: '10px',
                backgroundColor: '#ecfdf5',
                border: '1px solid #6ee7b7',
                color: '#065f46',
                fontSize: '0.95rem',
                lineHeight: '1.6',
                fontWeight: 600,
              }}
            >
              Founding / fee-free owner: you keep <strong>100%</strong> of rental totals for life. ATTACK! Your Adventure
              covers the platform fee and card processing on your payouts.
            </p>
          ) : (
            <p style={{ margin: '0 0 16px 0', color: '#6b7280', fontSize: '0.95rem', lineHeight: '1.6' }}>
              After you approve a request, the renter saves a card to secure the booking. Their card is charged 48 hours
              before the rental starts (or immediately if they book inside that window). When that charge processes, you
              receive {OWNER_SHARE_PERCENT}% in your connected Stripe account and ATTACK! Your Adventure keeps{' '}
              {PLATFORM_FEE_PERCENT}% (unless a site-wide promo is active).
            </p>
          )}
          {stripeStatus?.payoutsEnabled ? (
            <p style={{ margin: '0 0 12px 0', color: '#047857', fontWeight: '700' }}>
              Stripe connected — payouts are enabled.
            </p>
          ) : (
            <p style={{ margin: '0 0 12px 0', color: '#b45309', fontWeight: '600' }}>
              Connect Stripe to receive your {OWNER_SHARE_PERCENT}% share of rental payments.
            </p>
          )}
          {stripeMessage && (
            <p style={{ margin: '0 0 12px 0', color: '#1d4ed8', fontSize: '0.92rem' }}>
              {stripeMessage}
            </p>
          )}
          <button
            type="button"
            onClick={handleConnectStripe}
            disabled={stripeLoading}
            style={{
              padding: '12px 22px',
              backgroundColor: stripeLoading ? '#9ca3af' : '#635bff',
              color: 'white',
              border: 'none',
              borderRadius: '10px',
              fontWeight: '700',
              cursor: stripeLoading ? 'not-allowed' : 'pointer',
            }}
          >
            {stripeLoading
              ? 'Opening Stripe...'
              : stripeStatus?.payoutsEnabled
                ? 'Update Stripe Account'
                : 'Connect Stripe'}
          </button>
        </div>
      )}

      {isOwnProfile && (
        <div style={{ ...cardStyle, marginTop: '28px' }}>
          <h2 style={{ marginTop: 0, color: '#1f2937' }}>Local Expert</h2>
          <p style={{ margin: '0 0 16px 0', color: '#6b7280', fontSize: '0.95rem', lineHeight: '1.6' }}>
            Open to sharing local knowledge? If yes, members can message you for advice on activities and areas near you —
            not just gear rentals.
          </p>
          <p style={{ margin: '0 0 12px 0', fontWeight: '600', color: '#374151' }}>
            Would you like to be considered a local expert?
          </p>
          <div style={{ display: 'flex', gap: '12px', marginBottom: '12px' }}>
            {[
              { value: true, label: 'Yes' },
              { value: false, label: 'No' },
            ].map((option) => (
              <button
                key={option.label}
                type="button"
                onClick={() => setLocalExpert(option.value)}
                style={{
                  flex: 1,
                  padding: '12px 16px',
                  borderRadius: '10px',
                  border: localExpert === option.value ? '2px solid #059669' : '1px solid #d1d5db',
                  backgroundColor: localExpert === option.value ? '#ecfdf5' : '#ffffff',
                  color: localExpert === option.value ? '#047857' : '#4b5563',
                  fontWeight: '700',
                  cursor: 'pointer',
                }}
              >
                {option.label}
              </button>
            ))}
          </div>
          {localExpert && !location.trim() && (
            <p style={{ margin: 0, color: '#b45309', fontSize: '0.88rem' }}>
              Add your location below so people know which area you can speak to.
            </p>
          )}
        </div>
      )}

      {isOwnProfile && (
        <div style={{ ...cardStyle, marginTop: '28px' }}>
          <h2 style={{ marginTop: 0, color: '#1f2937' }}>Your Name</h2>
          <p style={{ margin: '0 0 16px 0', color: '#6b7280', fontSize: '0.9rem', lineHeight: '1.5' }}>
            Your name is saved on your account. Only your first name is shown publicly on your profile,
            gear listings, messages, and reviews.
          </p>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Display Name</label>
          <input
            type="text"
            placeholder="e.g. Alex Johnson"
            value={publicName}
            onChange={(e) => setPublicName(e.target.value)}
            style={{ ...inputStyle, marginBottom: '8px' }}
          />
          <p style={{ margin: 0, color: '#9ca3af', fontSize: '0.82rem' }}>
            Other members will only see your first name — for example, &quot;Alex&quot; from Alex Johnson.
          </p>
        </div>
      )}

      <div style={{ ...cardStyle, marginTop: '28px' }}>
        <h2 style={{ marginTop: 0, color: '#1f2937' }}>About {isOwnProfile ? 'Me' : getFirstName(displayName)}</h2>

        {isOwnProfile ? (
          <>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Location</label>
            <input
              type="text"
              placeholder="City, State"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              style={{ ...inputStyle, marginBottom: '16px' }}
            />

            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Phone</label>
            <input
              type="tel"
              placeholder="Phone number"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              style={{ ...inputStyle, marginBottom: '16px' }}
            />

            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>About Me</label>
            <textarea
              placeholder="Tell renters about yourself, your experience with outdoor gear, and what makes sharing easy with you..."
              value={aboutMe}
              onChange={(e) => setAboutMe(e.target.value)}
              style={{ ...inputStyle, minHeight: '140px', resize: 'vertical', marginBottom: '20px' }}
            />

            <button
              onClick={handleSaveProfile}
              disabled={saving}
              style={{
                padding: '14px 28px',
                backgroundColor: saving ? '#9ca3af' : '#3b82f6',
                color: 'white',
                border: 'none',
                borderRadius: '10px',
                fontWeight: '600',
                cursor: saving ? 'not-allowed' : 'pointer',
              }}
            >
              {saving ? 'Saving...' : 'Save Profile'}
            </button>
          </>
        ) : (
          <p style={{ lineHeight: '1.7', color: '#374151', whiteSpace: 'pre-wrap' }}>
            {profile.aboutMe || 'This member has not added an about section yet.'}
          </p>
        )}
      </div>

      {gearList.length > 0 && (
        <div style={{ ...cardStyle, marginTop: '28px' }}>
          <h2 style={{ marginTop: 0, color: '#1f2937' }}>
            {isOwnProfile ? 'My Listed Gear' : `${firstName}'s Gear`}
          </h2>
          <div style={{ display: 'grid', gap: '16px' }}>
            {gearList.map((gear) => (
              <Link
                key={gear.id}
                to={`/gear/${gear.id}`}
                style={{
                  display: 'flex',
                  gap: '16px',
                  padding: '16px',
                  borderRadius: '12px',
                  border: '1px solid #e5e7eb',
                  textDecoration: 'none',
                  color: 'inherit',
                  backgroundColor: '#f9fafb',
                }}
              >
                <img
                  src={gear.mainPicture || GEAR_IMAGE_PLACEHOLDER}
                  alt={gear.name}
                  style={{ width: '80px', height: '80px', borderRadius: '10px', objectFit: 'cover' }}
                />
                <div>
                  <h4 style={{ margin: '0 0 6px 0', color: '#1f2937' }}>{gear.name}</h4>
                  <p style={{ margin: 0, color: '#6b7280' }}>
                    ${gear.pricePerDay || '—'} / day · {gear.category}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {renterReviews.length > 0 && (
        <div style={{ ...cardStyle, marginTop: '28px' }}>
          <h2 style={{ marginTop: 0, color: '#1f2937' }}>
            {isOwnProfile ? 'Reviews From Gear Owners' : 'Renter Reviews'}
          </h2>
          <p style={{ margin: '0 0 16px 0', color: '#6b7280', fontSize: '0.92rem' }}>
            Feedback from owners after completed rentals.
          </p>
          {renterReviews.map((review) => (
            <ReviewCard
              key={review.id}
              review={review}
              currentUserId={currentUser?.uid}
              showGear
              showRentalDates
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default UserProfile;
export { getFirstName, getPublicName };